import { FiTrash2, FiMenu } from "react-icons/fi";

import { cn } from "utils/helpers";
import { QuoteTableCell } from "./QuoteTableCell";
import type { QuoteTableCellProps } from "./QuoteTableCell";
import type { QuoteTableColumn, ColumnHeader } from "./quote-table.types";
import { CELL_BORDER, COLUMN_STYLE } from "./quote-table.types";
import type { RfxQuoteItem } from "../../Submission/types/quote.type";

export interface QuoteTableRowProps {
	cellProps: Omit<QuoteTableCellProps, "column" | "item" | "isPrimaryDescription">;
	columns: QuoteTableColumn[];
	dragHandleProps?: Record<string, unknown>;
	headers: ColumnHeader[];
	index: number;
	item: RfxQuoteItem;
	primaryDescriptionColId: number | null;
	showDeleteAction: boolean;
	showDragHandle: boolean;
	showZeroButton: boolean;
	onDeleteClick?: (rowId: number) => void;
	onZeroClick?: (rowId: number) => void;
}

const QuoteTableRow: React.FC<QuoteTableRowProps> = ({
	cellProps,
	columns,
	dragHandleProps,
	headers,
	index,
	item,
	onDeleteClick,
	onZeroClick,
	primaryDescriptionColId,
	showDeleteAction,
	showDragHandle,
	showZeroButton,
}) => {
	return (
		<>
			{showZeroButton && (
				<td
					className={cn(CELL_BORDER, "text-center bg-gray-50")}
					style={{ width: "3%" }}
				>
					<button
						type="button"
						className="rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-800"
						onClick={() => onZeroClick?.(item.id)}
						data-testid={`zero-row-${item.id}`}
					>
						zero
					</button>
				</td>
			)}
			<td
				className={cn(
					CELL_BORDER,
					"text-center font-roboto text-xs font-normal text-primary-500 bg-gray-50"
				)}
				style={{ height: "35px" }}
			>
				<div className="flex items-center justify-center gap-1">
					{showDragHandle && (
						<div
							{...dragHandleProps}
							className="cursor-grab text-gray-400"
							data-testid={`row-drag-handle-${item.id}`}
						>
							<FiMenu size={12} />
						</div>
					)}
					<span>{index + 1}</span>
				</div>
			</td>
			{headers
				.filter((h) => h.columnId)
				.map((header) => {
					const col = columns.find((c) => c.id === header.columnId);
					if (!col) {
						return null;
					}
					return (
						<td
							key={header.key}
							className={cn(CELL_BORDER, COLUMN_STYLE)}
						>
							<QuoteTableCell
								{...cellProps}
								column={col}
								item={item}
								isPrimaryDescription={col.id === primaryDescriptionColId}
							/>
						</td>
					);
				})}
			{showDeleteAction && (
				<td
					className={cn(CELL_BORDER, "text-center")}
					style={{ width: "40px" }}
				>
					<button
						type="button"
						className="rounded p-0.5 text-red-400 hover:bg-red-50 hover:text-red-600"
						onClick={() => onDeleteClick?.(item.id)}
						title="Delete row"
						aria-label={`Delete row ${index + 1}`}
						data-testid={`delete-row-${item.id}`}
					>
						<FiTrash2 size={12} />
					</button>
				</td>
			)}
		</>
	);
};

export { QuoteTableRow };
